"use client";

import { cn } from "@/lib/utils/general-utils";
import React from "react";

export type CardVariant =
  | "default"
  | "info"
  | "warning"
  | "success"
  | "danger"
  | "shape-analytics"
  | "professional";
export type CardSize = "sm" | "md" | "lg" | "xl";
export type CardPadding = "none" | "sm" | "md" | "lg";
export type CardShadow = "none" | "sm" | "md" | "lg";

export interface CardProps {
  children?: React.ReactNode;
  variant?: CardVariant;
  size?: CardSize;
  title?: string;
  description?: string;
  footer?: React.ReactNode;
  className?: string;
  padding?: CardPadding;
  shadow?: CardShadow;
  loading?: boolean;
  interactive?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** @deprecated Animation removed in shadcn migration. Prop kept for backward compat. */
  animate?: boolean;
  /** @deprecated Animation removed in shadcn migration. Prop kept for backward compat. */
  animationDelay?: number;
  role?: React.AriaRole;
  tabIndex?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

const variantClasses: Record<CardVariant, string> = {
  default: "bg-white border border-border",
  info: "bg-blue-50 border border-blue-200",
  warning: "bg-amber-50 border border-amber-200",
  success: "bg-green-50 border border-green-200",
  danger: "bg-red-50 border border-red-200",
  "shape-analytics": "bg-gray-50 border border-gray-200",
  professional: "bg-white border border-gray-200",
};

const variantTitleColors: Record<CardVariant, string> = {
  default: "text-gray-900",
  info: "text-blue-900",
  warning: "text-amber-900",
  success: "text-green-900",
  danger: "text-red-900",
  "shape-analytics": "text-gray-900",
  professional: "text-gray-900",
};

const variantDescColors: Record<CardVariant, string> = {
  default: "text-gray-600",
  info: "text-blue-700",
  warning: "text-amber-700",
  success: "text-green-700",
  danger: "text-red-700",
  "shape-analytics": "text-gray-600",
  professional: "text-gray-600",
};

const variantBorderColors: Record<CardVariant, string> = {
  default: "border-gray-200",
  info: "border-blue-200",
  warning: "border-amber-200",
  success: "border-green-200",
  danger: "border-red-200",
  "shape-analytics": "border-gray-200",
  professional: "border-gray-200",
};

const paddingClasses: Record<CardPadding, string> = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

const shadowClasses: Record<CardShadow, string> = {
  none: "",
  sm: "shadow-sm",
  md: "shadow-md",
  lg: "shadow-lg",
};

const sizeClasses: Record<CardSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};

export const Card = React.memo<CardProps>(
  ({
    children,
    variant = "default",
    size,
    title,
    description,
    footer,
    className,
    padding = "none",
    shadow = "sm",
    loading = false,
    interactive = false,
    onClick,

    animate: _animate,

    animationDelay: _animationDelay,
    ...props
  }) => {
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      try {
        onClick?.(event);
      } catch (error) {
        console.error("Card click error:", error);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (onClick && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
      }
      props.onKeyDown?.(event);
    };

    if (loading) {
      return (
        <div
          className={cn("animate-pulse bg-gray-200 rounded-lg h-32", className)}
        />
      );
    }

    return (
      <div
        className={cn(
          "relative overflow-hidden rounded-lg transition-all duration-200",
          variantClasses[variant],
          shadowClasses[shadow],
          paddingClasses[padding],
          size && sizeClasses[size],
          (interactive || onClick) && [
            "cursor-pointer",
            variant === "default" && "hover:bg-gray-50",
            variant === "shape-analytics" && "hover:bg-gray-100",
            variant !== "default" &&
              variant !== "shape-analytics" &&
              "hover:shadow-md",
          ],
          onClick &&
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          className,
        )}
        onClick={handleClick}
        onKeyDown={onClick ? handleKeyDown : props.onKeyDown}
        tabIndex={onClick ? 0 : props.tabIndex}
        role={onClick ? "button" : props.role}
        {...props}
      >
        {(title || description) && (
          <div
            className={cn(
              "mb-4",
              padding === "none" && "px-6 pt-6",
              padding !== "none" && "-mt-2 mb-6",
            )}
          >
            {title && (
              <h3
                className={cn(
                  "text-lg font-semibold",
                  variantTitleColors[variant],
                )}
              >
                {title}
              </h3>
            )}
            {description && (
              <p className={cn("mt-1 text-sm", variantDescColors[variant])}>
                {description}
              </p>
            )}
          </div>
        )}

        <div
          className={cn(
            (title || description) && padding === "none" && "px-6",
            footer && padding === "none" && "pb-0",
          )}
        >
          {children}
        </div>

        {footer && (
          <div
            className={cn(
              "mt-6 border-t",
              variantBorderColors[variant],
              padding === "none" ? "px-6 py-4" : "pt-6 -mb-2",
            )}
          >
            {footer}
          </div>
        )}
      </div>
    );
  },
);

Card.displayName = "Card";

export interface CardSubComponentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader = React.memo<CardSubComponentProps>(
  ({ children, className }) => (
    <div className={cn("mb-6 pb-4 border-b border-gray-200", className)}>
      {children}
    </div>
  ),
);

export const CardTitle = React.memo<CardSubComponentProps>(
  ({ children, className }) => (
    <h3 className={cn("text-lg font-bold text-gray-900 mb-1", className)}>
      {children}
    </h3>
  ),
);

export const CardDescription = React.memo<CardSubComponentProps>(
  ({ children, className }) => (
    <p className={cn("text-sm text-text-tertiary", className)}>{children}</p>
  ),
);

export const CardContent = React.memo<CardSubComponentProps>(
  ({ children, className }) => (
    <div className={cn("px-6 py-4", className)}>{children}</div>
  ),
);

export const CardFooter = React.memo<CardSubComponentProps>(
  ({ children, className }) => (
    <div
      className={cn("px-6 py-4 border-t border-gray-200 bg-gray-50", className)}
    >
      {children}
    </div>
  ),
);

CardHeader.displayName = "CardHeader";
CardTitle.displayName = "CardTitle";
CardDescription.displayName = "CardDescription";
CardContent.displayName = "CardContent";
CardFooter.displayName = "CardFooter";
